import React, { createContext, useContext, useState, useCallback, useEffect, ReactNode } from 'react';
import { apiFetch } from '../lib/apiFetch';
import { useAuth } from './AuthContext';
import { toast } from '../lib/toast';
import {
    TareaConRelaciones, EstadoTarea, ApiResponse, KanbanSwimlane, FASE_ORDER,
    Prioridad, Tamanio, ClaseServicio, WipLimite, Metricas, BacklogItem, Politica,
    Cliente, EstadoCliente, CfdPunto, BloqueosReporte, ThroughputPunto, WipAgingItem,
    AlertasReporte, StandupResumen, ReplenishSugerencia, RetrospectivaData, DocumentoCliente,
    Proyecto, FaseProyecto
} from '../types';

export type ResultadoEliminacionCliente = 'definitivo' | 'desactivado' | 'no_encontrado';

interface KanbanContextType {
    swimlanes: KanbanSwimlane[];
    loading: boolean;
    error: string | null;
    fetchKanbanBoard: () => Promise<void>;
    actualizarEstadoTarea: (tareaId: number, nuevoEstado: EstadoTarea) => Promise<boolean>;
    actualizarTarea: (tareaId: number, datos: { titulo?: string; descripcion?: string | null; prioridad?: Prioridad; fecha_limite?: string | null; responsable?: string | null; tamanio?: Tamanio; clase_servicio?: ClaseServicio; bloqueado?: boolean; motivo_bloqueo?: string | null }) => Promise<boolean>;
    eliminarTarea: (tareaId: number) => Promise<boolean>;
    marcarCompletado: (tareaId: number) => Promise<boolean>;
    registrarPedidoCliente: (data: { cliente_id: number; titulo: string; descripcion?: string; tipo_solicitud: string; fecha_limite?: string | null; prioridad?: Prioridad; estado?: EstadoTarea }) => Promise<boolean>;
    clientes: { id: number; nombre: string; empresa: string; color: string }[];
    fetchClientes: () => Promise<void>;
    clientesTodos: Cliente[];
    fetchClientesTodos: () => Promise<void>;
    crearCliente: (datos: { nombre: string; empresa: string; color?: string }) => Promise<boolean>;
    actualizarCliente: (id: number, datos: { nombre?: string; empresa?: string; color?: string; estado?: EstadoCliente }) => Promise<boolean>;
    eliminarCliente: (id: number) => Promise<{ success: boolean; resultado?: ResultadoEliminacionCliente; message?: string }>;
    wipLimites: WipLimite[];
    fetchWipLimites: () => Promise<void>;
    actualizarWipLimite: (estado: EstadoTarea, limite: number | null) => Promise<boolean>;
    metricas: Metricas | null;
    fetchMetricas: () => Promise<void>;
    backlogAntiguo: BacklogItem[];
    fetchBacklog: () => Promise<void>;
    archivarTarea: (tareaId: number) => Promise<boolean>;
    politicas: Politica | null;
    fetchPoliticas: () => Promise<void>;
    actualizarPoliticas: (contenido: string) => Promise<boolean>;
    cfd: CfdPunto[];
    fetchCfd: (dias?: number) => Promise<void>;
    bloqueos: BloqueosReporte | null;
    fetchBloqueos: () => Promise<void>;
    throughputHistory: ThroughputPunto[];
    fetchThroughputHistory: (dias?: number) => Promise<void>;
    wipAging: WipAgingItem[];
    fetchWipAging: () => Promise<void>;
    alertas: AlertasReporte | null;
    fetchAlertas: () => Promise<void>;
    standup: StandupResumen | null;
    fetchStandup: () => Promise<void>;
    replenishSuggestion: ReplenishSugerencia | null;
    fetchReplenishSuggestion: (count?: number) => Promise<void>;
    retrospectiva: RetrospectivaData | null;
    fetchRetrospectiva: (desde?: string, hasta?: string) => Promise<void>;
    documentosPorTarea: Record<number, DocumentoCliente[]>;
    fetchDocumentosTarea: (tareaId: number) => Promise<DocumentoCliente[]>;
    subirDocumentoCliente: (clienteId: number, archivo: File, tareaId?: number | null) => Promise<DocumentoCliente | null>;
    eliminarDocumento: (documentoId: number) => Promise<boolean>;
    vincularDocumentoTarea: (documentoId: number, tareaId: number | null) => Promise<boolean>;
    fetchProyectosCliente: (clienteId: number) => Promise<Proyecto[]>;
    crearProyecto: (clienteId: number, datos: { nombre_proyecto: string; fase: FaseProyecto }) => Promise<boolean>;
}

const KanbanContext = createContext<KanbanContextType | undefined>(undefined);

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:4000/api';

const emptySwimlanes: KanbanSwimlane[] = FASE_ORDER.map(fase => ({
    fase,
    tareas: {
        backlog: [],
        por_hacer: [],
        en_progreso: [],
        esperando_cliente: [],
        testing: [],
        produccion: []
    }
}));

export const KanbanProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const { usuario } = useAuth();
    const [swimlanes, setSwimlanes] = useState<KanbanSwimlane[]>(emptySwimlanes);
    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);
    const [clientes, setClientes] = useState<{ id: number; nombre: string; empresa: string; color: string }[]>([]);
    const [clientesTodos, setClientesTodos] = useState<Cliente[]>([]);
    const [wipLimites, setWipLimites] = useState<WipLimite[]>([]);
    const [metricas, setMetricas] = useState<Metricas | null>(null);
    const [backlogAntiguo, setBacklogAntiguo] = useState<BacklogItem[]>([]);
    const [politicas, setPoliticas] = useState<Politica | null>(null);
    const [cfd, setCfd] = useState<CfdPunto[]>([]);
    const [bloqueos, setBloqueos] = useState<BloqueosReporte | null>(null);
    const [throughputHistory, setThroughputHistory] = useState<ThroughputPunto[]>([]);
    const [wipAging, setWipAging] = useState<WipAgingItem[]>([]);
    const [alertas, setAlertas] = useState<AlertasReporte | null>(null);
    const [standup, setStandup] = useState<StandupResumen | null>(null);
    const [replenishSuggestion, setReplenishSuggestion] = useState<ReplenishSugerencia | null>(null);
    const [retrospectiva, setRetrospectiva] = useState<RetrospectivaData | null>(null);
    const [documentosPorTarea, setDocumentosPorTarea] = useState<Record<number, DocumentoCliente[]>>({});

    const fetchKanbanBoard = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const response = await apiFetch(`${API_BASE_URL}/tareas/kanban-swimlanes`);
            const result: ApiResponse<KanbanSwimlane[]> = await response.json();

            if (result.success && result.data) {
                setSwimlanes(result.data);
            } else {
                setError(result.error || 'Error al cargar el tablero');
            }
        } catch (err) {
            setError('No se pudo conectar con el servidor');
            console.error('Error al cargar Kanban:', err);
        } finally {
            setLoading(false);
        }
    }, []);

    const actualizarEstadoTarea = useCallback(async (tareaId: number, nuevoEstado: EstadoTarea): Promise<boolean> => {
        try {
            const response = await apiFetch(`${API_BASE_URL}/tareas/${tareaId}/estado`, {
                method: 'PATCH',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ estado: nuevoEstado })
            });

            const result: ApiResponse<{ id: number; estado: EstadoTarea }> = await response.json();

            if (result.success) {
                setSwimlanes(prev => prev.map(swimlane => {
                    const nuevasTareas = { ...swimlane.tareas };
                    let tareaMovida: TareaConRelaciones | undefined;

                    (Object.keys(nuevasTareas) as EstadoTarea[]).forEach(estado => {
                        nuevasTareas[estado] = nuevasTareas[estado].filter(t => {
                            if (t.id === tareaId) {
                                tareaMovida = { ...t, estado: nuevoEstado };
                                return false;
                            }
                            return true;
                        });
                    });

                    if (tareaMovida) {
                        nuevasTareas[nuevoEstado] = [...nuevasTareas[nuevoEstado], tareaMovida];
                    }

                    return { ...swimlane, tareas: nuevasTareas };
                }));
                return true;
            }
            if (response.status === 409 && result.error) {
                toast.warning(
                    '⛔ WIP Limit',
                    `${result.error}\n\nCambiá la clase a "expedito" si necesitás saltarte el límite.`
                );
            } else if (result.error) {
                toast.error('Error', result.error);
            }
            return false;
        } catch (err) {
            console.error('Error al actualizar tarea:', err);
            return false;
        }
    }, []);

    const actualizarTarea = useCallback(async (
        tareaId: number,
        datos: { titulo?: string; descripcion?: string | null; prioridad?: Prioridad; fecha_limite?: string | null; responsable?: string | null; tamanio?: Tamanio; clase_servicio?: ClaseServicio; bloqueado?: boolean; motivo_bloqueo?: string | null }
    ): Promise<boolean> => {
        try {
            const response = await apiFetch(`${API_BASE_URL}/tareas/${tareaId}`, {
                method: 'PATCH',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(datos)
            });

            const result: ApiResponse<TareaConRelaciones> = await response.json();

            if (result.success && result.data) {
                setSwimlanes(prev => prev.map(swimlane => {
                    const nuevasTareas = { ...swimlane.tareas };
                    (Object.keys(nuevasTareas) as EstadoTarea[]).forEach(estado => {
                        nuevasTareas[estado] = nuevasTareas[estado].map(t =>
                            t.id === tareaId ? result.data! : t
                        );
                    });
                    return { ...swimlane, tareas: nuevasTareas };
                }));
                return true;
            }
            return false;
        } catch (err) {
            console.error('Error al actualizar tarea:', err);
            return false;
        }
    }, []);

    const eliminarTarea = useCallback(async (tareaId: number): Promise<boolean> => {
        try {
            const response = await apiFetch(`${API_BASE_URL}/tareas/${tareaId}`, {
                method: 'DELETE'
            });

            const result: ApiResponse<{ id: number }> = await response.json();

            if (result.success) {
                setSwimlanes(prev => prev.map(swimlane => {
                    const nuevasTareas = { ...swimlane.tareas };
                    (Object.keys(nuevasTareas) as EstadoTarea[]).forEach(estado => {
                        nuevasTareas[estado] = nuevasTareas[estado].filter(t => t.id !== tareaId);
                    });
                    return { ...swimlane, tareas: nuevasTareas };
                }));
                return true;
            }
            return false;
        } catch (err) {
            console.error('Error al eliminar tarea:', err);
            return false;
        }
    }, []);

    const marcarCompletado = useCallback(async (tareaId: number): Promise<boolean> => {
        try {
            const response = await apiFetch(`${API_BASE_URL}/tareas/${tareaId}/completar`, {
                method: 'POST'
            });

            const result: ApiResponse<{ id: number; estado: EstadoTarea }> = await response.json();

            if (result.success) {
                await fetchKanbanBoard();
                return true;
            }
            return false;
        } catch (err) {
            console.error('Error al marcar tarea como completada:', err);
            return false;
        }
    }, [fetchKanbanBoard]);

    const registrarPedidoCliente = useCallback(async (data: {
        cliente_id: number;
        titulo: string;
        descripcion?: string;
        tipo_solicitud: string;
        fecha_limite?: string | null;
        prioridad?: Prioridad;
        estado?: EstadoTarea;
    }): Promise<boolean> => {
        try {
            const payload: any = { ...data };
            if (data.fecha_limite) {
                payload.fecha_limite = new Date(data.fecha_limite).toISOString();
            }
            const response = await apiFetch(`${API_BASE_URL}/tareas/registrar-pedido`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });

            const result: ApiResponse<{ id: number; proyecto_id: number }> = await response.json();

            if (result.success) {
                await fetchKanbanBoard();
                return true;
            }
            return false;
        } catch (err) {
            console.error('Error al registrar pedido:', err);
            return false;
        }
    }, [fetchKanbanBoard]);

    const fetchClientes = useCallback(async () => {
        try {
            const response = await apiFetch(`${API_BASE_URL}/clientes`);
            const result: ApiResponse<{ id: number; nombre: string; empresa: string; color: string }[]> = await response.json();

            if (result.success && result.data) {
                setClientes(result.data);
            }
        } catch (err) {
            console.error('Error al cargar clientes:', err);
        }
    }, []);

    const fetchClientesTodos = useCallback(async () => {
        try {
            const response = await apiFetch(`${API_BASE_URL}/clientes?incluirInactivos=true`);
            const result: ApiResponse<Cliente[]> = await response.json();

            if (result.success && result.data) {
                setClientesTodos(result.data);
            }
        } catch (err) {
            console.error('Error al cargar clientes:', err);
        }
    }, []);

    const crearCliente = useCallback(async (datos: { nombre: string; empresa: string; color?: string }): Promise<boolean> => {
        try {
            const response = await apiFetch(`${API_BASE_URL}/clientes`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(datos)
            });

            const result: ApiResponse<Cliente> = await response.json();

            if (result.success) {
                await fetchClientesTodos();
                await fetchClientes();
                return true;
            }
            return false;
        } catch (err) {
            console.error('Error al crear cliente:', err);
            return false;
        }
    }, [fetchClientesTodos, fetchClientes]);

    const actualizarCliente = useCallback(async (
        id: number,
        datos: { nombre?: string; empresa?: string; color?: string; estado?: EstadoCliente }
    ): Promise<boolean> => {
        try {
            const response = await apiFetch(`${API_BASE_URL}/clientes/${id}`, {
                method: 'PATCH',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(datos)
            });

            const result: ApiResponse<Cliente> = await response.json();

            if (result.success) {
                await fetchClientesTodos();
                await fetchClientes();
                await fetchKanbanBoard();
                return true;
            }
            return false;
        } catch (err) {
            console.error('Error al actualizar cliente:', err);
            return false;
        }
    }, [fetchClientesTodos, fetchClientes, fetchKanbanBoard]);

    const eliminarCliente = useCallback(async (id: number): Promise<{ success: boolean; resultado?: ResultadoEliminacionCliente; message?: string }> => {
        try {
            const response = await apiFetch(`${API_BASE_URL}/clientes/${id}`, {
                method: 'DELETE'
            });

            const result: ApiResponse<{ id: number; resultado: ResultadoEliminacionCliente }> = await response.json();

            if (result.success && result.data) {
                await fetchClientesTodos();
                await fetchClientes();
                await fetchKanbanBoard();
                return { success: true, resultado: result.data.resultado, message: result.message };
            }
            return { success: false, message: result.error };
        } catch (err) {
            console.error('Error al eliminar cliente:', err);
            return { success: false };
        }
    }, [fetchClientesTodos, fetchClientes, fetchKanbanBoard]);

    const fetchWipLimites = useCallback(async () => {
        try {
            const response = await apiFetch(`${API_BASE_URL}/configuracion/wip-limites`);
            const result: ApiResponse<WipLimite[]> = await response.json();

            if (result.success && result.data) {
                setWipLimites(result.data);
            }
        } catch (err) {
            console.error('Error al cargar límites WIP:', err);
        }
    }, []);

    const actualizarWipLimite = useCallback(async (estado: EstadoTarea, limite: number | null): Promise<boolean> => {
        try {
            const response = await apiFetch(`${API_BASE_URL}/configuracion/wip-limites/${estado}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ limite })
            });

            const result: ApiResponse<WipLimite> = await response.json();

            if (result.success) {
                setWipLimites(prev => {
                    const existe = prev.some(w => w.estado === estado);
                    if (existe) {
                        return prev.map(w => w.estado === estado ? { estado, limite } : w);
                    }
                    return [...prev, { estado, limite }];
                });
                return true;
            }
            return false;
        } catch (err) {
            console.error('Error al actualizar límite WIP:', err);
            return false;
        }
    }, []);

    const fetchMetricas = useCallback(async () => {
        try {
            const response = await apiFetch(`${API_BASE_URL}/tareas/metricas`);
            const result: ApiResponse<Metricas> = await response.json();

            if (result.success && result.data) {
                setMetricas(result.data);
            }
        } catch (err) {
            console.error('Error al cargar métricas:', err);
        }
    }, []);

    const fetchBacklog = useCallback(async () => {
        try {
            const response = await apiFetch(`${API_BASE_URL}/tareas/backlog`);
            const result: ApiResponse<BacklogItem[]> = await response.json();

            if (result.success && result.data) {
                setBacklogAntiguo(result.data);
            }
        } catch (err) {
            console.error('Error al cargar el backlog:', err);
        }
    }, []);

    const archivarTarea = useCallback(async (tareaId: number): Promise<boolean> => {
        try {
            const response = await apiFetch(`${API_BASE_URL}/tareas/${tareaId}/archivar`, {
                method: 'PATCH'
            });

            const result: ApiResponse<{ id: number }> = await response.json();

            if (result.success) {
                setBacklogAntiguo(prev => prev.filter(t => t.id !== tareaId));
                setSwimlanes(prev => prev.map(swimlane => {
                    const nuevasTareas = { ...swimlane.tareas };
                    (Object.keys(nuevasTareas) as EstadoTarea[]).forEach(estado => {
                        nuevasTareas[estado] = nuevasTareas[estado].filter(t => t.id !== tareaId);
                    });
                    return { ...swimlane, tareas: nuevasTareas };
                }));
                return true;
            }
            return false;
        } catch (err) {
            console.error('Error al archivar tarea:', err);
            return false;
        }
    }, []);

    const fetchPoliticas = useCallback(async () => {
        try {
            const response = await apiFetch(`${API_BASE_URL}/configuracion/politicas`);
            const result: ApiResponse<Politica> = await response.json();

            if (result.success && result.data) {
                setPoliticas(result.data);
            }
        } catch (err) {
            console.error('Error al cargar políticas:', err);
        }
    }, []);

    const actualizarPoliticas = useCallback(async (contenido: string): Promise<boolean> => {
        try {
            const response = await apiFetch(`${API_BASE_URL}/configuracion/politicas`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ contenido })
            });

            const result: ApiResponse<{ contenido: string }> = await response.json();

            if (result.success) {
                setPoliticas(prev => prev ? { ...prev, contenido } : { contenido, actualizado_en: new Date().toISOString() });
                return true;
            }
            return false;
        } catch (err) {
            console.error('Error al actualizar políticas:', err);
            return false;
        }
    }, []);

    const fetchCfd = useCallback(async (dias: number = 30) => {
        try {
            const response = await apiFetch(`${API_BASE_URL}/tareas/cfd?dias=${dias}`);
            const result: ApiResponse<CfdPunto[]> = await response.json();
            if (result.success && result.data) setCfd(result.data);
        } catch (err) {
            console.error('Error al cargar CFD:', err);
        }
    }, []);

    const fetchBloqueos = useCallback(async () => {
        try {
            const response = await apiFetch(`${API_BASE_URL}/tareas/bloqueos`);
            const result: ApiResponse<BloqueosReporte> = await response.json();
            if (result.success && result.data) setBloqueos(result.data);
        } catch (err) {
            console.error('Error al cargar bloqueos:', err);
        }
    }, []);

    const fetchThroughputHistory = useCallback(async (dias: number = 90) => {
        try {
            const response = await apiFetch(`${API_BASE_URL}/tareas/throughput-history?dias=${dias}`);
            const result: ApiResponse<ThroughputPunto[]> = await response.json();
            if (result.success && result.data) setThroughputHistory(result.data);
        } catch (err) {
            console.error('Error al cargar histórico de throughput:', err);
        }
    }, []);

    const fetchWipAging = useCallback(async () => {
        try {
            const response = await apiFetch(`${API_BASE_URL}/tareas/wip-aging`);
            const result: ApiResponse<WipAgingItem[]> = await response.json();
            if (result.success && result.data) setWipAging(result.data);
        } catch (err) {
            console.error('Error al cargar WIP aging:', err);
        }
    }, []);

    const fetchAlertas = useCallback(async () => {
        try {
            const response = await apiFetch(`${API_BASE_URL}/tareas/alertas`);
            const result: ApiResponse<AlertasReporte> = await response.json();
            if (result.success && result.data) setAlertas(result.data);
        } catch (err) {
            console.error('Error al cargar alertas:', err);
        }
    }, []);

    const fetchStandup = useCallback(async () => {
        try {
            const response = await apiFetch(`${API_BASE_URL}/tareas/standup`);
            const result: ApiResponse<StandupResumen> = await response.json();
            if (result.success && result.data) setStandup(result.data);
        } catch (err) {
            console.error('Error al cargar standup:', err);
        }
    }, []);

    const fetchReplenishSuggestion = useCallback(async (count: number = 3) => {
        try {
            const response = await apiFetch(`${API_BASE_URL}/tareas/replenish-suggestion?count=${count}`);
            const result: ApiResponse<ReplenishSugerencia> = await response.json();
            if (result.success && result.data) setReplenishSuggestion(result.data);
        } catch (err) {
            console.error('Error al cargar sugerencias:', err);
        }
    }, []);

    const fetchRetrospectiva = useCallback(async (desde?: string, hasta?: string) => {
        try {
            const params = new URLSearchParams();
            if (desde) params.set('desde', desde);
            if (hasta) params.set('hasta', hasta);
            const qs = params.toString();
            const response = await apiFetch(`${API_BASE_URL}/tareas/retrospective${qs ? '?' + qs : ''}`);
            const result: ApiResponse<RetrospectivaData> = await response.json();
            if (result.success && result.data) setRetrospectiva(result.data);
        } catch (err) {
            console.error('Error al cargar retrospectiva:', err);
        }
    }, []);

    const fetchDocumentosTarea = useCallback(async (tareaId: number): Promise<DocumentoCliente[]> => {
        try {
            const response = await apiFetch(`${API_BASE_URL}/documentos/tarea/${tareaId}`);
            const result: ApiResponse<DocumentoCliente[]> = await response.json();
            if (result.success && result.data) {
                setDocumentosPorTarea(prev => ({ ...prev, [tareaId]: result.data! }));
                return result.data;
            }
            return [];
        } catch (err) {
            console.error('Error al cargar documentos de tarea:', err);
            return [];
        }
    }, []);

    const subirDocumentoCliente = useCallback(async (
        clienteId: number,
        archivo: File,
        tareaId?: number | null
    ): Promise<DocumentoCliente | null> => {
        try {
            const formData = new FormData();
            formData.append('archivo', archivo);
            if (tareaId) formData.append('tarea_id', String(tareaId));

            const response = await apiFetch(`${API_BASE_URL}/clientes/${clienteId}/documentos`, {
                method: 'POST',
                body: formData
            });
            const result: ApiResponse<DocumentoCliente> = await response.json();
            if (result.success && result.data) {
                if (tareaId) {
                    setDocumentosPorTarea(prev => ({
                        ...prev,
                        [tareaId]: [result.data!, ...(prev[tareaId] || [])]
                    }));
                }
                return result.data;
            }
            return null;
        } catch (err) {
            console.error('Error al subir documento:', err);
            return null;
        }
    }, []);

    const eliminarDocumento = useCallback(async (documentoId: number): Promise<boolean> => {
        try {
            const response = await apiFetch(`${API_BASE_URL}/documentos/${documentoId}`, { method: 'DELETE' });
            const result: ApiResponse<{ id: number }> = await response.json();
            if (result.success) {
                setDocumentosPorTarea(prev => {
                    const next = { ...prev };
                    Object.keys(next).forEach(tid => {
                        next[Number(tid)] = next[Number(tid)].filter(d => d.id !== documentoId);
                    });
                    return next;
                });
                return true;
            }
            return false;
        } catch (err) {
            console.error('Error al eliminar documento:', err);
            return false;
        }
    }, []);

    const vincularDocumentoTarea = useCallback(async (
        documentoId: number,
        tareaId: number | null
    ): Promise<boolean> => {
        try {
            const response = await apiFetch(`${API_BASE_URL}/documentos/${documentoId}/tarea`, {
                method: 'PATCH',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ tarea_id: tareaId })
            });
            const result: ApiResponse<DocumentoCliente> = await response.json();
            if (result.success) {
                setDocumentosPorTarea(prev => {
                    const next = { ...prev };
                    if (tareaId !== null && result.data) {
                        const updated = next[tareaId] ? [result.data, ...next[tareaId].filter(d => d.id !== documentoId)] : [result.data];
                        next[tareaId] = updated;
                    }
                    Object.keys(next).forEach(tid => {
                        next[Number(tid)] = next[Number(tid)].filter(d => d.id !== documentoId);
                    });
                    if (tareaId !== null && result.data) {
                        next[tareaId] = [result.data, ...(next[tareaId] || [])];
                    }
                    return next;
                });
                return true;
            }
            return false;
        } catch (err) {
            console.error('Error al vincular documento:', err);
            return false;
        }
    }, []);

    const fetchProyectosCliente = useCallback(async (clienteId: number): Promise<Proyecto[]> => {
        try {
            const response = await apiFetch(`${API_BASE_URL}/clientes/${clienteId}/proyectos`);
            const result: ApiResponse<Proyecto[]> = await response.json();
            return result.success && result.data ? result.data : [];
        } catch (err) {
            console.error('Error al obtener proyectos:', err);
            return [];
        }
    }, []);

    const crearProyecto = useCallback(async (
        clienteId: number,
        datos: { nombre_proyecto: string; fase: FaseProyecto }
    ): Promise<boolean> => {
        try {
            const response = await apiFetch(`${API_BASE_URL}/clientes/${clienteId}/proyectos`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(datos)
            });
            const result: ApiResponse<{ id: number }> = await response.json();
            return result.success;
        } catch (err) {
            console.error('Error al crear proyecto:', err);
            return false;
        }
    }, []);

    // Solo se precarga lo que el usuario puede ver — si no, cada carga
    // inicial dispara 403 contra módulos que no tiene habilitados (ruido
    // en la consola, además de requests de más). Gerente ve todo siempre,
    // mismo criterio que requirePermiso en el backend.
    const puede = (modulo: string): boolean =>
        usuario?.rol === 'gerente' || Boolean(usuario?.permisos_modulos?.includes(modulo as any));

    useEffect(() => {
        if (puede('kanban')) fetchKanbanBoard();
        if (puede('clientes')) fetchClientes();
        if (puede('configuracion')) fetchWipLimites();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [usuario?.id]);

    useEffect(() => {
        if (puede('clientes')) fetchClientesTodos();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [usuario?.id]);

    return (
        <KanbanContext.Provider
            value={{
                swimlanes,
                loading,
                error,
                fetchKanbanBoard,
                actualizarEstadoTarea,
                actualizarTarea,
                eliminarTarea,
                marcarCompletado,
                registrarPedidoCliente,
                clientes,
                fetchClientes,
                clientesTodos,
                fetchClientesTodos,
                crearCliente,
                actualizarCliente,
                eliminarCliente,
                wipLimites,
                fetchWipLimites,
                actualizarWipLimite,
                metricas,
                fetchMetricas,
                backlogAntiguo,
                fetchBacklog,
                archivarTarea,
                politicas,
                fetchPoliticas,
                actualizarPoliticas,
                cfd,
                fetchCfd,
                bloqueos,
                fetchBloqueos,
                throughputHistory,
                fetchThroughputHistory,
                wipAging,
                fetchWipAging,
                alertas,
                fetchAlertas,
                standup,
                fetchStandup,
                replenishSuggestion,
                fetchReplenishSuggestion,
                retrospectiva,
                fetchRetrospectiva,
                documentosPorTarea,
                fetchDocumentosTarea,
                subirDocumentoCliente,
                eliminarDocumento,
                vincularDocumentoTarea,
                fetchProyectosCliente,
                crearProyecto
            }}
        >
            {children}
        </KanbanContext.Provider>
    );
};

export const useKanban = (): KanbanContextType => {
    const context = useContext(KanbanContext);
    if (!context) {
        throw new Error('useKanban debe usarse dentro de un KanbanProvider');
    }
    return context;
};
